import { useRect } from '@reach/rect';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { type Annotation } from '@air/api/types';

import {
  AnnotationsAvatarsContainer,
  type AnnotationsAvatarsContainerProps,
} from '~/components/AnnotationsV2/AnnotationsAvatarsContainer/AnnotationsAvatarsContainer';
import { AnnotationsKeyShortcuts } from '~/components/AnnotationsV2/components/AnnotationsKeyShortcuts';
import { type MultiAnnotationLayerProps } from '~/components/AnnotationsV2/components/MultiAnnotationLayer';
import { useAnnotationV2Store } from '~/components/AnnotationsV2/providers/AnnotationV2Provider';
import { annotationTooltipText } from '~/components/AnnotationsV2/shared/constants';
import { useDiscussionPanelContext } from '~/components/AssetModal/shared/context/DiscussionsPanelContext/DiscussionsPanelContext';
import { useFullscreenContext } from '~/components/AssetModal/shared/context/FullscreenContext';
import { AnnotationTooltip } from '~/components/AssetModalV2/Visualizer/Annotations/AnnotationTooltip';
import { useMouseTooltip } from '~/components/AssetModalV2/Visualizer/Annotations/useMouseTooltip';
import { BOUNDING_BOX_LAYER_Z_INDEX } from '~/components/BoundingBox/constants';
import { QueryParamNames } from '~/constants/search';
import { activeTimestampParamSelector } from '~/store/router/selectors';
import { isCypressRun, setQueryParams } from '~/utils/PathUtils';

// Dynamic imports to avoid SSR issues with react-konva
const DiscussionAnnotationsDisplay = dynamic(
  () =>
    import('~/components/AnnotationsV2/components/DiscussionAnnotationsDisplay').then(
      (mod) => mod.DiscussionAnnotationsDisplay,
    ),
  { ssr: false },
);

const MultiAnnotationLayer = dynamic(
  () => import('~/components/AnnotationsV2/components/MultiAnnotationLayer').then((mod) => mod.MultiAnnotationLayer),
  { ssr: false },
);

export type AnnotationsV2Props = {
  pageNumber: number;
  keyShortcutsEnabled: boolean;
  canDrawAnnotation: boolean;
  renderKeyboardShortcuts?: boolean;
  topOffset?: number;
  hideAnnotations?: boolean;
  width?: number;
  height?: number;
} & Pick<AnnotationsAvatarsContainerProps, 'AnnotationsAvatars' | 'AnnotationComment'> &
  Pick<MultiAnnotationLayerProps, 'FloatingCommentForm'>;

/**
 * V2: Annotations component that supports multiple annotations per discussion
 * Uses AnnotationsAvatarsContainer for rendering discussions with multiple annotations
 */
export const AnnotationsV2 = memo(
  ({
    keyShortcutsEnabled,
    canDrawAnnotation,
    pageNumber,
    topOffset = 0,
    renderKeyboardShortcuts = true,
    hideAnnotations,
    width,
    height,
    AnnotationsAvatars,
    AnnotationComment,
    FloatingCommentForm,
  }: AnnotationsV2Props) => {
    const { isFullscreen } = useFullscreenContext();
    const containerRef = useRef<HTMLDivElement>(null);
    const annotationTooltipRef = useRef<HTMLDivElement | null>(null);

    const containerRect = useRect(containerRef);

    const annotationType = useAnnotationV2Store((state) => state.annotationType);
    const annotations = useAnnotationV2Store((state) => state.annotations);
    const isAnnotationsEnabled = useAnnotationV2Store((state) => state.isAnnotationsEnabled);
    const activeTimestamp = useSelector(activeTimestampParamSelector);
    const { setScrollToDiscussionId } = useDiscussionPanelContext();

    // Use passed dimensions directly (no more useResizeDetector issues)
    const containerWidth = width || 0;
    const containerHeight = height || 0;

    // Check if there are annotations for the current page
    const pageAnnotations = useMemo(() => {
      return annotations.filter((a: Annotation) => {
        if (a.type === 'box') {
          return a.pageNumber === pageNumber;
        }
        if (a.type === 'pencil') {
          return a.lines.some((line) => line.pageNumber === pageNumber);
        }

        return false;
      });
    }, [annotations, pageNumber]);

    useMouseTooltip({
      // This complains about Ref types but it works as expected
      containerRef: containerRef,
      tooltipRef: annotationTooltipRef,
      disabled: !canDrawAnnotation || pageAnnotations.length > 0,
      dependencies: [pageNumber],
    });

    const isDisabled = !canDrawAnnotation || !isAnnotationsEnabled;

    const annotationLayer = useMemo(() => {
      // Use MultiAnnotationLayer for box annotations
      return (
        <MultiAnnotationLayer
          containerWidth={containerWidth ?? 0}
          containerHeight={containerHeight ?? 0}
          containerLeft={containerRect?.left ?? 0}
          containerRef={containerRef}
          disabled={isDisabled}
          FloatingCommentForm={FloatingCommentForm}
          pageNumber={pageNumber}
        />
      );
    }, [FloatingCommentForm, containerHeight, containerRect?.left, containerWidth, isDisabled, pageNumber]);

    const onAvatarClick = useCallback<AnnotationsAvatarsContainerProps['onAvatarClick']>(
      (discussion) => {
        setQueryParams({ [QueryParamNames.discussionId]: [discussion.id] }, 'replace');
        // Also set the scroll target in context to scroll to the discussion in the comments panel
        setScrollToDiscussionId(discussion.id);
      },
      [setScrollToDiscussionId],
    );

    if (isFullscreen) {
      return null;
    }

    return (
      <>
        <div
          data-testid="ANNOTATION_LAYER"
          ref={containerRef}
          className={classNames('absolute left-1/2 max-w-full -translate-x-1/2 -translate-y-1/2 transform', {
            '[&>canvas]:pointer-events-none': isDisabled,
            '[&>canvas]:cursor-box-annotation': !isDisabled && annotationType === 'box',
            '[&>canvas]:cursor-pencil-annotation': !isDisabled && annotationType !== 'box',
          })}
          style={{
            zIndex: BOUNDING_BOX_LAYER_Z_INDEX,
            height: containerHeight,
            width: containerWidth,
            minWidth: containerWidth,
            minHeight: containerHeight,
            top: `calc(50% + ${topOffset}px)`,
            pointerEvents: isDisabled ? 'none' : 'auto',
          }}
        >
          {/* Layer 1: Read-only annotation shapes (lowest z-index) */}
          {!hideAnnotations && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
              <DiscussionAnnotationsDisplay
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                pageNumber={pageNumber}
                highlightMode="bright"
                showAllAnnotations={false}
              />
            </div>
          )}

          {/* Layer 2: Drawing layer (middle z-index) */}
          {!hideAnnotations && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 2 }}>
              {isAnnotationsEnabled || pageAnnotations.length > 0 ? annotationLayer : null}
            </div>
          )}

          {/* Layer 3: Avatar markers (highest z-index - must be clickable) */}
          {!!containerWidth && !!containerHeight && !hideAnnotations && (
            <div className="pointer-events-none absolute inset-0" style={{ zIndex: 3 }}>
              <AnnotationsAvatarsContainer
                AnnotationComment={AnnotationComment}
                AnnotationsAvatars={AnnotationsAvatars}
                containerWidth={containerWidth}
                containerHeight={containerHeight}
                containerLeft={containerRect?.left ?? 0}
                containerTop={containerRect?.top ?? 0}
                timestamp={activeTimestamp}
                pageNumber={pageNumber}
                onAvatarClick={onAvatarClick}
                hideAnnotations={pageAnnotations.length > 0 || hideAnnotations}
              />
            </div>
          )}
          {isAnnotationsEnabled && annotationType && pageAnnotations.length === 0 && (
            <AnnotationTooltip
              text={annotationTooltipText[annotationType]}
              innerRef={annotationTooltipRef}
              shouldDisplay={canDrawAnnotation}
            />
          )}
        </div>
        {renderKeyboardShortcuts && <AnnotationsKeyShortcuts enabled={keyShortcutsEnabled} />}
        {!!containerWidth && !!containerHeight && isCypressRun() && <div data-testid="CYPRESS_ANNOTATIONS_READY" />}
      </>
    );
  },
);

AnnotationsV2.displayName = 'AnnotationsV2';
